import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthActions } from '../../../shared/store/auth/auth.actions';
import { AuthSelectors } from '../../../shared/store/auth/auth.selectors';
import { Router } from '@angular/router';
import { NotificationUtil } from '../../../shared/util/notification.util';
import { catchError } from 'rxjs/operators';
import { tap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthFacadeService {
  private store: Store = inject(Store);
  private router: Router = inject(Router);
  private notificationUtil: NotificationUtil = inject(NotificationUtil);

  public isProcessing$ = this.store.select(AuthSelectors.isProcessing);

  public login(username: string, password: string) {
    return this.store.dispatch(new AuthActions.Login(username, password)).pipe(
      tap(() => {
        this.notificationUtil.success('DOMAIN.AUTH.MESSAGE.LOGIN');
        this.router.navigate(['/home']);
      }),
      catchError(error => {
        if (error.message === 'SET_PASSWORD') {
          this.router.navigate(['/auth/set-password']);
        } else {
          this.notificationUtil.error(error?.message);
          this.store.dispatch(new AuthActions.Logout());
        }

        return throwError(() => error);
      })
    );
  }

  public loginWithGoogle() {
    return this.store.dispatch(new AuthActions.LoginWithGoogle());
  }

  public loginWithFacebook() {
    return this.store.dispatch(new AuthActions.LoginWithFacebook());
  }

  public confirmSocialFaceebok() {
    tap(() => {
      this.notificationUtil.success('DOMAIN.AUTH.MESSAGE.LOGIN');
      this.router.navigate(['/home']);
    }),
      catchError(error => {
        this.notificationUtil.error(error?.message);
        this.store.dispatch(new AuthActions.Logout());
        return throwError(() => error);
      });
  }

  public changePassword(currentPassword: string, password: string) {
    return this.store
      .dispatch(new AuthActions.ChangePassword(currentPassword, password))
      .pipe(
        tap(() => {
          this.notificationUtil.success('DOMAIN.AUTH.MESSAGE.CHANGE_PASSWORD');
        }),
        catchError(error => {
          this.notificationUtil.error(error?.message);

          return throwError(() => error);
        })
      );
  }

  public setPassword(password: string) {
    return this.store.dispatch(new AuthActions.SetPassword(password)).pipe(
      tap(() => {
        this.notificationUtil.success('DOMAIN.AUTH.MESSAGE.LOGIN');
        this.router.navigate(['/']);
      }),
      catchError(error => {
        this.notificationUtil.error(error?.message);

        return throwError(() => error);
      })
    );
  }

  public resetPassword(username: string) {
    return this.store.dispatch(new AuthActions.ResetPassword(username)).pipe(
      tap(() => {
        this.notificationUtil.info('DOMAIN.AUTH.MESSAGE.RESET_PASSWORD');
        this.router.navigate(['/auth/reset-password-confirmation', username]);
      }),
      catchError(error => {
        this.notificationUtil.error(error?.message);

        return throwError(() => error);
      })
    );
  }

  public confirmResetPassword(
    username: string,
    code: string,
    password: string
  ) {
    return this.store
      .dispatch(new AuthActions.ConfirmResetPassword(username, code, password))
      .pipe(
        tap(() => {
          this.notificationUtil.success(
            'DOMAIN.AUTH.MESSAGE.CONFIRMATION_RESET_PASSWORD'
          );
          this.router.navigate(['/auth/login']);
        }),
        catchError(error => {
          this.notificationUtil.error(error?.message);

          return throwError(() => error);
        })
      );
  }

  public logout() {
    this.store.dispatch(new AuthActions.Logout()).subscribe(() => {
      this.router.navigate(['/auth/login']);
      this.notificationUtil.info('DOMAIN.AUTH.MESSAGE.LOGOUT');
    });
  }
}
